import { Component, inject, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { CommonModule } from '@angular/common';
import { SpendEfficiencyResponse } from '../../../../components/my-competition-screen/types';
import { SelectItem } from '../../select-menu/select-menu.component';
import { ATTENTION_METRIC_NAME } from '../../../types/common';
import { PerformanceComparisonMetric, SpendEfficiencyXAxisMetrics, SpendEfficiencyYAxisMetrics } from '../../../../components/my-brand-screen/types';
import { Format, UtilService } from '../../../../services/util.service';
import {LoaderComponent} from '../../loader/loader.component';
import {NoWidgetDataComponent} from '../../../../components/my-brand-screen/no-widget-data/no-widget-data.component';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-brand-logo-graph',
  standalone: true,
  imports: [CommonModule, HighchartsChartModule, LoaderComponent, NoWidgetDataComponent, AssetVersionPipe],
  templateUrl: './brand-logo-graph.component.html',
  styleUrl: './brand-logo-graph.component.scss',
})
export class BrandLogoGraphComponent implements OnChanges {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  utilService = inject(UtilService);
  @Input() loading = false;
  @Input() data?: SpendEfficiencyResponse;
  @Input() xMetric?: SelectItem | string | null = { key: PerformanceComparisonMetric.SPEND, label: 'Estimated Spend' };
  @Input() yMetric?: SelectItem | string | null = { key: PerformanceComparisonMetric.ATTENTION, label: ATTENTION_METRIC_NAME };

  private resolvedXMetric: SelectItem | null = null;
  private resolvedYMetric: SelectItem | null = null;

  ngOnChanges(): void {
    this.resolvedXMetric = this.resolveMetric(this.xMetric, SpendEfficiencyXAxisMetrics);
    this.resolvedYMetric = this.resolveMetric(this.yMetric, SpendEfficiencyYAxisMetrics);
    this.createChartOptions();
  }

  public get isCpmSelected(): boolean {
    return this.resolvedXMetric?.key === PerformanceComparisonMetric.CPM;
  }

  get isDataAvailable(): boolean {
    if (!this.data?.brands?.length) return false;
    const xMetricKey = this.resolvedXMetric?.key;
    const shouldApplyThreshold =
      xMetricKey === PerformanceComparisonMetric.SPEND ||
      xMetricKey === PerformanceComparisonMetric.IMPRESSIONS;
    if (!shouldApplyThreshold) return true;
    return this.isAnyMetricValueAboveThreshold(this.data, xMetricKey, 100);
  }

  private isAnyMetricValueAboveThreshold(data: SpendEfficiencyResponse, metric: string, threshold: number): boolean {
    return data.brands.some(brand => {
      const metricEntry = brand.values.find(v => v.metric === metric);
      return metricEntry !== undefined && metricEntry.value > threshold;
    });
  }

  private resolveMetric(metric: SelectItem | string | undefined | null, options: SelectItem[]): SelectItem | null {
    if (!metric) return null;
    if (typeof metric === 'string') {
      return options.find(o => o.key === metric) || null;
    }
    return metric;
  }

  private getFormat(metricKey: string | undefined): Format {
    switch (metricKey) {
      case PerformanceComparisonMetric.SPEND:
        return 'shortCurrencyNumber';
      case PerformanceComparisonMetric.CPM:
        return 'currency';
      case PerformanceComparisonMetric.IMPRESSIONS:
        return 'shortNumber';
      case PerformanceComparisonMetric.ATTENTION:
        return 'shortPercentage';
      case PerformanceComparisonMetric.CTR:
        return 'percentage';
      default:
        return 'shortNumber';
    }
  }

  private transformData(response: SpendEfficiencyResponse | undefined, xMetric: SelectItem | null, yMetric: SelectItem | null): Highcharts.PointOptionsObject[] {
    if (!response || !xMetric || !yMetric) return [];
    return response.brands.map((brand) => {
      const xData = brand.values.find((v) => v.metric === xMetric.key);
      const yData = brand.values.find((v) => v.metric === yMetric.key);

      const obj: Highcharts.PointOptionsObject = {
        x: xData ? xData.value : 0,
        y: yData ? yData.value : 0,
        name: brand.brandName,
        marker: {
          enabled: true,
          symbol: `url(${brand.brandLogo})`,
          width: 30,
          height: 30,
        }
      };
      return obj;
    });
  };

  private createChartOptions(): void {
    const xMetric = this.resolvedXMetric;
    const yMetric = this.resolvedYMetric;
    this.chartOptions = {
      chart: {
        type: 'scatter',
        backgroundColor: 'transparent',
        spacing: [20, 20, 80, 5],
        events: {
          load: function (this: Highcharts.Chart) {
            this.container.querySelectorAll('image').forEach((img) => {
              img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            });
          },
          render: function (this: any) {
            if (this._xMidLine) this._xMidLine.destroy();
            if (this._yMidLine) this._yMidLine.destroy();

            const xMidPx = this.xAxis[0].toPixels(((this.xAxis[0].min ?? 0) + (this.xAxis[0].max ?? 0)) / 2);
            const yMidPx = this.yAxis[0].toPixels(((this.yAxis[0].min ?? 0) + (this.yAxis[0].max ?? 0)) / 2);

            this._xMidLine = this.renderer
              .path(['M', xMidPx, this.plotTop, 'L', xMidPx, this.plotTop + this.plotHeight])
              .attr({ stroke: '#CDD1D7', 'stroke-width': 1, zIndex: 1 })
              .add();

            this._yMidLine = this.renderer
              .path(['M', this.plotLeft, yMidPx, 'L', this.plotLeft + this.plotWidth, yMidPx])
              .attr({ stroke: '#CDD1D7', 'stroke-width': 1, zIndex: 1 })
              .add();
          }
        }
      },
      title: {
        text: undefined
      },
      xAxis: {
        title: {
          text: this.resolvedXMetric?.label,
          style: {
            fontWeight: '600',
            color: '#1F2123',
            fontSize: '12px'
          }
        },
        labels: {
          formatter: (function (utilService: UtilService, metric: SelectItem | null, getFormat: (key: string | undefined) => Format) {
            return function (this: any) {
              const format = getFormat(metric?.key);
              if (this.value === 0) return format === 'shortCurrencyNumber' || format === 'currency' ? '$0' : '0';
              return utilService.formatWidgetValue(this.value as number, format, 2) as string;
            };
          })(this.utilService, xMetric, this.getFormat.bind(this)),
          style: {
            color: '#1F2123',
            fontWeight: '400',
            fontSize: '12px'
          }
        },
        gridLineWidth: 0,
        lineWidth: 0,
        tickWidth: 0,
        min: 0
      },
      yAxis: {
        title: {
          text: this.resolvedYMetric?.label,
          style: {
            fontWeight: '600',
            color: '#1F2123',
            fontSize: '12px'
          }
        },
        labels: {
          formatter: (function (utilService: UtilService, metric: SelectItem | null, getFormat: (key: string | undefined) => Format) {
            return function (this: any) {
              const format = getFormat(metric?.key);
              return utilService.formatWidgetValue(this.value as number, format, 2) as string;
            };
          })(this.utilService, yMetric, this.getFormat.bind(this)),
          style: {
            color: '#1F2123',
            fontWeight: '400',
            fontSize: '12px'
          }
        },
        gridLineWidth: 0,
        lineWidth: 0,
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: 'transparent',
        formatter: (function (utilService: UtilService, xMetric: SelectItem | null, yMetric: SelectItem | null, getFormat: (key: string | undefined) => Format) {
          return function (this: any) {
            const point = this.point;
            const xFormat = getFormat(xMetric?.key);
            const yFormat = getFormat(yMetric?.key);

            const xValueFormatted = utilService.formatWidgetValue(point.x, xFormat, 2);
            const yValueFormatted = utilService.formatWidgetValue(point.y, yFormat, 2);

            return `
      <div style="
        background: white;
        border: 1px solid #E2E8F0;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        font-family: 'Poppins', sans-serif;">
        <div style="font-weight: 700; font-size: 12px; margin-bottom: 8px; color:#34363A;">
          ${point.name}
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="color: #34363A; font-weight: 400;">${yMetric?.label}</span>
          <span style="font-weight: 600; padding-left: 20px; color:#4E5258;">${yValueFormatted}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span style="color: #34363A; font-weight: 400;">${xMetric?.label}</span>
          <span style="font-weight: 600; padding-left: 20px; color:#4E5258;">${xValueFormatted}</span>
        </div>
      </div>
    `;
          };
        })(this.utilService, xMetric, yMetric, this.getFormat.bind(this))
      },
      plotOptions: {
        scatter: {
          states: {
            hover: {
              enabled: true,
              halo: null
            }
          }
        }
      },
      series: [{
        type: 'scatter',
        name: 'Brands',
        zIndex: 5,
        data: this.transformData(this.data, this.resolvedXMetric, this.resolvedYMetric)
      }]
    };
  }
}
